// Генератор quiz упражнений
import { fetchOpenAIText } from './openaiClient';

export const generateQuizFromText = async (text, level = 'B1', count = 6) => {
  try {
    console.log('Starting quiz generation from text:', text.substring(0, 100) + '...');
    const levelComplexity = {
      'A1': 'very simple, short sentences with basic high-frequency vocabulary; concrete topics; avoid clauses',
      'A2': 'simple sentences with everyday vocabulary; mostly concrete topics; minimal subordinate clauses',
      'B1': 'intermediate sentences with varied vocabulary; mix of simple and compound sentences; plausible distractors',
      'B2': 'more complex structures; abstract topics allowed; nuanced distractors; avoid giveaway wording',
      'C1': 'advanced structures and sophisticated vocabulary; abstract/academic topics; high-quality distractors',
      'C2': 'near-native academic style; highly complex structures; precise terminology; challenging distractors'
    };
    
    const prompt = `Create a multiple choice quiz based on the following text.
    CEFR Level: ${level} (${levelComplexity[level] || 'intermediate sentences with varied vocabulary'})
    
    Return ONLY a JSON object with this exact structure:
    {
      "title": "Quiz Title",
      "questions": [
        {
          "id": 1,
          "question": "Question text?",
          "options": [
            {"id": "a", "text": "Option A", "isCorrect": false},
            {"id": "b", "text": "Option B", "isCorrect": true},
            {"id": "c", "text": "Option C", "isCorrect": false},
            {"id": "d", "text": "Option D", "isCorrect": false}
          ]
        }
      ]
    }
    
    Rules:
    - CONSTRAINT: The "questions" array MUST contain exactly ${count} items.
    - Each question must have exactly 4 options (a, b, c, d)
    - Only one option is correct per question
    - Difficulty and vocabulary must strictly match CEFR ${level}
    - Make distractors plausible at CEFR ${level}; avoid "All of the above/None of the above"
    - Test comprehension, vocabulary, and key concepts relevant to the text
    - Keep option lengths similar to avoid bias
    - Ensure valid, strictly formatted JSON
    
    Text: ${text}`;

    console.log('Making API request to OpenAI (via proxy)...');
    const content = await fetchOpenAIText({
      prompt,
      temperature: 0.7,
      maxTokens: 1500,
    });
    
    if (content) {
      console.log('API content received:', content.substring(0, 200) + '...');
      
      // Try to extract JSON from the response
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const quizData = JSON.parse(jsonMatch[0]);
        console.log('Quiz generated successfully:', quizData);
        return quizData;
      }
      
      // If no JSON found, try to parse the entire content
      const quizData = JSON.parse(content);
      console.log('Quiz generated successfully (full content):', quizData);
      return quizData;
    }
    
    throw new Error("No response from API");
  } catch (error) {
    console.error("Error generating quiz:", error);
    console.error("Error details:", {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    
    // Return a fallback quiz structure
    return {
      title: "Quiz Exercise",
      questions: [
        {
          id: 1,
          question: "What is the main topic of this text?",
          options: [
            { id: "a", text: "Technology", isCorrect: false },
            { id: "b", text: "Education", isCorrect: true },
            { id: "c", text: "Sports", isCorrect: false },
            { id: "d", text: "Food", isCorrect: false }
          ]
        },
        {
          id: 2,
          question: "Which of the following best describes the text?",
          options: [
            { id: "a", text: "A story", isCorrect: false },
            { id: "b", text: "An article", isCorrect: true },
            { id: "c", text: "A poem", isCorrect: false },
            { id: "d", text: "A letter", isCorrect: false }
          ]
        }
      ]
    };
  }
};

// Генератор quiz по теме
export const generateQuizFromTopic = async (topic, level = 'B1', count = 6) => {
  try {
    const levelComplexity = {
      'A1': 'very simple, short sentences with basic high-frequency vocabulary; concrete topics; avoid clauses',
      'A2': 'simple sentences with everyday vocabulary; mostly concrete topics; minimal subordinate clauses',
      'B1': 'intermediate sentences with varied vocabulary; mix of simple and compound sentences; plausible distractors',
      'B2': 'more complex structures; abstract topics allowed; nuanced distractors; avoid giveaway wording',
      'C1': 'advanced structures and sophisticated vocabulary; abstract/academic topics; high-quality distractors',
      'C2': 'near-native academic style; highly complex structures; precise terminology; challenging distractors'
    };
    const prompt = `Create a multiple choice quiz about the topic: "${topic}".
    CEFR Level: ${level} (${levelComplexity[level] || 'intermediate sentences with varied vocabulary'})
    Return ONLY a JSON object with this exact structure:
    {
      "title": "Quiz about [Topic]",
      "questions": [
        {
          "id": 1,
          "question": "Question text?",
          "options": [
            {"id": "a", "text": "Option A", "isCorrect": false},
            {"id": "b", "text": "Option B", "isCorrect": true},
            {"id": "c", "text": "Option C", "isCorrect": false},
            {"id": "d", "text": "Option D", "isCorrect": false}
          ]
        }
      ]
    }
    
    Rules:
    - CONSTRAINT: The "questions" array MUST contain exactly ${count} items.
    - Each question must have exactly 4 options (a, b, c, d)
    - Only one option is correct per question
    - Difficulty and vocabulary must strictly match CEFR ${level}
    - Make distractors plausible at CEFR ${level}; avoid "All of the above/None of the above"
    - Test knowledge, understanding, and application
    - Keep option lengths similar to avoid bias
    - Ensure valid, strictly formatted JSON
    - Make questions engaging and educational`;

    const content = await fetchOpenAIText({
      prompt,
      temperature: 0.7,
      maxTokens: 1500,
    });
    
    if (content) {
      
      // Try to extract JSON from the response
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const quizData = JSON.parse(jsonMatch[0]);
        return quizData;
      }
      
      // If no JSON found, try to parse the entire content
      return JSON.parse(content);
    }
    
    throw new Error("No response from API");
  } catch (error) {
    console.error("Error generating quiz:", error);
    
    // Return a fallback quiz structure
    return {
      title: `Quiz about ${topic}`,
      questions: [
        {
          id: 1,
          question: `What is the main focus of ${topic}?`,
          options: [
            { id: "a", text: "Learning", isCorrect: true },
            { id: "b", text: "Playing", isCorrect: false },
            { id: "c", text: "Sleeping", isCorrect: false },
            { id: "d", text: "Eating", isCorrect: false }
          ]
        },
        {
          id: 2,
          question: `Which of the following is related to ${topic}?`,
          options: [
            { id: "a", text: "Knowledge", isCorrect: true },
            { id: "b", text: "Food", isCorrect: false },
            { id: "c", text: "Sports", isCorrect: false },
            { id: "d", text: "Music", isCorrect: false }
          ]
        }
      ]
    };
  }
};

// Создание пустого quiz
export const createEmptyQuiz = (title = "New Quiz") => {
  return {
    title: title,
    questions: [
      {
        id: 1,
        question: "Enter your question here?",
        options: [
          { id: "a", text: "Option A", isCorrect: false },
          { id: "b", text: "Option B", isCorrect: true },
          { id: "c", text: "Option C", isCorrect: false },
          { id: "d", text: "Option D", isCorrect: false }
        ]
      }
    ]
  };
};

// Валидация quiz данных
export const validateQuizData = (quizData) => {
  if (!quizData || typeof quizData !== 'object') {
    return { isValid: false, error: 'Invalid quiz data structure' };
  }

  if (!quizData.title || typeof quizData.title !== 'string') {
    return { isValid: false, error: 'Quiz title is required' };
  }

  if (!Array.isArray(quizData.questions) || quizData.questions.length === 0) {
    return { isValid: false, error: 'Quiz must have at least one question' };
  }

  for (let i = 0; i < quizData.questions.length; i++) {
    const question = quizData.questions[i];
    
    if (!question.id || !question.question || !Array.isArray(question.options)) {
      return { isValid: false, error: `Question ${i + 1} has invalid structure` };
    }

    if (question.options.length !== 4) {
      return { isValid: false, error: `Question ${i + 1} must have exactly 4 options` };
    }

    const correctOptions = question.options.filter(opt => opt.isCorrect);
    if (correctOptions.length !== 1) {
      return { isValid: false, error: `Question ${i + 1} must have exactly one correct answer` };
    }

    for (let j = 0; j < question.options.length; j++) {
      const option = question.options[j];
      if (!option.id || !option.text || typeof option.isCorrect !== 'boolean') {
        return { isValid: false, error: `Question ${i + 1}, option ${j + 1} has invalid structure` };
      }
    }
  }

  return { isValid: true };
};

// Генератор Fill in the gaps упражнений
export const generateFillInTheGapsExercise = async (words, level) => {
  // Parsed once up front so both the prompt and the post-generation
  // validation/retry agree on exactly what was asked for.
  const parsedWords = words
    .split(/[,\n]/)
    .map(word => word.trim())
    .filter(word => word.length > 0);

  const levelSpecs = {
    A1: '4–7 word sentences; only present simple/continuous, "to be"; concrete everyday vocabulary.',
    A2: '6–10 word sentences; past simple, "going to" future, basic modals. AVOID perfect tenses, conditionals.',
    B1: '8–14 word sentences; past continuous, present perfect, first conditional, common phrasal verbs.',
    B2: '10–18 word sentences; all perfect tenses, second/third conditional, passive voice, reported speech.',
    C1: '12–22 word sentences; complex subordinate clauses, inversion, academic/journalistic register.',
    C2: '14–26 word sentences; near-native register, subtle idiomatic/discourse distinctions.',
  };

  const buildPrompt = (missingWords) => `Create a "Fill in the gaps" exercise using these words/phrases: ${missingWords.join(', ')}.

    Level: ${level} (${levelSpecs[level] || 'intermediate sentences with varied vocabulary'})

    Return ONLY a JSON object with this exact structure (the markdown
    asterisks below mark the target word; they are formatting, not
    literal text):
    {
      "title": "Fill in the Gaps Exercise",
      "level": "${level}",
      "sentences": [
        { "id": 1, "sentence": "She **went** to the store yesterday.", "gap": "went" },
        { "id": 2, "sentence": "The students are **studying** for their final exam.", "gap": "studying" }
      ]
    }

    Rules:
    - Create exactly ${missingWords.length} sentences (one per given word/phrase, in the same order).
    - Wrap the target word/phrase in Markdown bold (two asterisks on each side, like **went**) so the embed can split on it.
    - Never write the literal words "bold", "word", or "phrase" anywhere inside the sentence text or the gap field. The asterisks are just a marker.
    - Each "gap" field is the exact target string with no asterisks, exactly as it appears between the two ** markers in the sentence.
    - Each sentence must contain exactly ONE **target**; no underscores, no blanks, no placeholders.
    - Sentences must be natural, grammatical, and at the ${level} level of complexity.

    CRITICAL GRAMMAR RULE: You MUST use the grammatically correct form of each word/phrase in the sentence.
    - If the given word is a verb base form (e.g. "go"), conjugate it correctly for the sentence context (e.g. "goes", "went", "is going").
    - If the given word is a noun, use the correct form (singular/plural, possessive if needed).
    - If the given word is an adjective, use comparative/superlative if the context requires it.
    - The form wrapped in ** in the sentence AND the value of "gap" must MATCH each other character-for-character. They are the inflected form used in this sentence, not the base form given by the teacher.
    - Choose sentence contexts where the adaptation is natural and unambiguous.`;

  const runOnce = async (missingWords) => {
    const content = await fetchOpenAIText({
      prompt: buildPrompt(missingWords),
      temperature: 0.7,
      maxTokens: 2000,
    });
    if (!content) return null;
    const contentStr = content.trim();
    try {
      const jsonMatch = contentStr.match(/\{[\s\S]*\}/);
      return JSON.parse(jsonMatch ? jsonMatch[0] : contentStr);
    } catch (_) {
      return null;
    }
  };

  let exerciseData = await runOnce(parsedWords);

  // If the model under-delivered (fewer sentences than words given), ask
  // for just the missing ones instead of silently shipping a short
  // exercise or — worse — falling back to generic filler text.
  if (exerciseData && Array.isArray(exerciseData.sentences) && exerciseData.sentences.length < parsedWords.length) {
    const haveWords = new Set(exerciseData.sentences.map(s => s.gap));
    const missingWords = parsedWords.filter(w => !haveWords.has(w));
    if (missingWords.length > 0) {
      const fix = await runOnce(missingWords);
      if (fix && Array.isArray(fix.sentences)) {
        const maxId = exerciseData.sentences.reduce((m, s) => Math.max(m, s.id || 0), 0);
        exerciseData = {
          ...exerciseData,
          sentences: [
            ...exerciseData.sentences,
            ...fix.sentences.map((s, i) => ({ ...s, id: maxId + i + 1 })),
          ],
        };
      }
    }
  }

  if (!exerciseData || !Array.isArray(exerciseData.sentences) || exerciseData.sentences.length === 0) {
    throw new Error('Could not generate the exercise — the AI response was invalid. Please try again.');
  }

  return exerciseData;
};

// Создание пустого упражнения Fill in the gaps
export const createEmptyFillInTheGapsExercise = (title = "New Fill in the Gaps Exercise", level = "B1") => {
  return {
    title,
    level,
    sentences: [
      {
        id: 1,
        sentence: "This is a sample sentence with **example** for practice.",
        gap: "example"
      }
    ]
  };
};

// Валидация данных упражнения Fill in the gaps
export const validateFillInTheGapsData = (exerciseData) => {
  if (!exerciseData || typeof exerciseData !== 'object') {
    return false;
  }
  
  if (!exerciseData.title || !exerciseData.level || !Array.isArray(exerciseData.sentences)) {
    return false;
  }
  
  return exerciseData.sentences.every(sentence => 
    sentence.id && 
    sentence.sentence && 
    sentence.gap &&
    typeof sentence.id === 'number' &&
    typeof sentence.sentence === 'string' &&
    typeof sentence.gap === 'string'
  );
}; 